import React, { useState, useEffect, useCallback } from 'react';
import { client } from '../../api/client';

interface AdType {
  id: number;
  name: string;
  slug: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

interface Subcategory {
  id: number;
  category_id: number;
  name: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  icon: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  subcategory_count?: number;
  property_count?: number;
  subcategories?: Subcategory[];
}

interface FieldOption {
  id: number;
  field_id: number;
  value: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

interface FieldDefinition {
  id: number;
  name: string;
  slug: string;
  field_type: string;
  filter_type: string;
  is_required: boolean;
  placeholder?: string;
  help_text?: string;
  sort_order: number;
  is_active: boolean;
  options?: FieldOption[];
}

interface CategoryField {
  id: number;
  ad_type_id: number | null;
  category_id: number;
  field_id: number;
  is_required: boolean;
  is_filterable: boolean;
  show_in_listing: boolean;
  show_in_detail: boolean;
  sort_order: number;
  ad_type_name?: string;
  category_name?: string;
  field_name?: string;
  field_type?: string;
  filter_type?: string;
}

type ActiveSection = 'ad-types' | 'categories' | 'fields' | 'field-mapping';

export function CategoriesTab() {
  const [activeSection, setActiveSection] = useState<ActiveSection>('categories');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [adTypes, setAdTypes] = useState<AdType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [categoryFields, setCategoryFields] = useState<CategoryField[]>([]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'adType' | 'category' | 'subcategory' | 'field' | 'fieldOption'>('category');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [parentItem, setParentItem] = useState<any>(null);
  
  // Form state
  const [formData, setFormData] = useState<any>({});
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Error state
  const [error, setError] = useState<string | null>(null);

  // Fetch data
  const fetchAdTypes = useCallback(async () => {
    try {
      const res = await client.api.fetch('/admin/ad-types');
      const data = await res.json();
      if (data.success) {
        setAdTypes(data.data || []);
      } else if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error('Error fetching ad types:', err);
      setError('Greška pri učitavanju tipova oglasa');
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await client.api.fetch('/admin/categories');
      const data = await res.json();
      if (data.success) {
        setCategories(data.data || []);
      } else if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Greška pri učitavanju kategorija');
    }
  }, []);

  const fetchFields = useCallback(async () => {
    try {
      const res = await client.api.fetch('/admin/fields');
      const data = await res.json();
      if (data.success) setFields(data.data || []);
    } catch (err) {
      console.error('Error fetching fields:', err);
    }
  }, []);

  const fetchCategoryFields = useCallback(async () => {
    try {
      const res = await client.api.fetch('/admin/category-fields');
      const data = await res.json();
      if (data.success) setCategoryFields(data.data || []);
    } catch (err) {
      console.error('Error fetching category fields:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAdTypes(), fetchCategories(), fetchFields(), fetchCategoryFields()])
      .finally(() => setLoading(false));
  }, [fetchAdTypes, fetchCategories, fetchFields, fetchCategoryFields]);

  // Modal handlers
  const openCreateModal = (type: typeof modalType, parent?: any) => {
    setModalType(type);
    setEditingItem(null);
    setParentItem(parent || null);
    setFormData({});
    setShowModal(true);
  };

  const openEditModal = (type: typeof modalType, item: any, parent?: any) => {
    setModalType(type);
    setEditingItem(item);
    setParentItem(parent || null);
    setFormData({ ...item });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setParentItem(null);
    setFormData({});
  };

  // Save handlers
  const handleSave = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      const method = editingItem ? 'PUT' : 'POST';
      
      switch (modalType) {
        case 'adType':
          endpoint = editingItem ? `/admin/ad-types/${editingItem.id}` : '/admin/ad-types';
          break;
        case 'category':
          endpoint = editingItem ? `/admin/categories/${editingItem.id}` : '/admin/categories';
          break;
        case 'subcategory':
          endpoint = editingItem ? `/admin/subcategories/${editingItem.id}` : '/admin/subcategories';
          if (!editingItem) formData.category_id = parentItem?.id;
          break;
        case 'field':
          endpoint = editingItem ? `/admin/fields/${editingItem.id}` : '/admin/fields';
          break;
        case 'fieldOption':
          endpoint = editingItem 
            ? `/admin/field-options/${editingItem.id}` 
            : `/admin/fields/${parentItem?.id}/options`;
          break;
      }
      
      const res = await client.api.fetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        closeModal();
        // Refresh data
        if (modalType === 'adType') fetchAdTypes();
        else if (modalType === 'category' || modalType === 'subcategory') fetchCategories();
        else if (modalType === 'field' || modalType === 'fieldOption') fetchFields();
      } else {
        alert(data.error || 'Greška pri čuvanju');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const handleDelete = async (type: typeof modalType, id: number) => {
    if (!confirm('Da li ste sigurni da želite da obrišete ovu stavku?')) return;
    
    setLoading(true);
    try {
      let endpoint = '';
      switch (type) {
        case 'adType': endpoint = `/admin/ad-types/${id}`; break;
        case 'category': endpoint = `/admin/categories/${id}`; break;
        case 'subcategory': endpoint = `/admin/subcategories/${id}`; break;
        case 'field': endpoint = `/admin/fields/${id}`; break;
        case 'fieldOption': endpoint = `/admin/field-options/${id}`; break;
      }
      
      const res = await client.api.fetch(endpoint, { method: 'DELETE' });
      const data = await res.json();
      
      if (data.success) {
        if (type === 'adType') fetchAdTypes();
        else if (type === 'category' || type === 'subcategory') fetchCategories();
        else if (type === 'field' || type === 'fieldOption') fetchFields();
      } else {
        alert(data.error || 'Greška pri brisanju');
      }
    } catch (err) {
      alert('Greška pri povezivanju sa serverom');
    } finally {
      setLoading(false);
    }
  };

  // Section navigation buttons
  const SectionTabs = () => (
    <div className="flex flex-wrap gap-2 mb-6">
      {[
        { key: 'ad-types', label: 'Tipovi oglasa', icon: '📋' },
        { key: 'categories', label: 'Kategorije & Podkategorije', icon: '🏠' },
        { key: 'fields', label: 'Polja / Filteri', icon: '📝' },
        { key: 'field-mapping', label: 'Mapiranje polja', icon: '🔗' },
      ].map(tab => (
        <button
          key={tab.key}
          onClick={() => setActiveSection(tab.key as ActiveSection)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeSection === tab.key
              ? 'bg-amber-500 text-white'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
          }`}
        >
          <span>{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  );

  // Ad Types Section
  const AdTypesSection = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Tipovi oglasa</h3>
        <button
          onClick={() => openCreateModal('adType')}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novi tip
        </button>
      </div>
      
      <div className="grid gap-4 md:grid-cols-3">
        {adTypes.map(type => (
          <div key={type.id} className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-semibold text-slate-900 dark:text-white">{type.name}</h4>
              <div className="flex gap-1">
                <button
                  onClick={() => openEditModal('adType', type)}
                  className="p-1 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete('adType', type.id)}
                  className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{type.description || 'Bez opisa'}</p>
            <div className="mt-2 text-xs text-slate-400">Slug: {type.slug}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // Categories Section
  const CategoriesSection = () => {
    const filteredCategories = categories.filter(cat =>
      cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="flex-1 min-w-[200px] max-w-md">
            <input
              type="text"
              placeholder="Pretraži kategorije..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </div>
          <button
            onClick={() => openCreateModal('category')}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nova Kategorija
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredCategories.map(category => (
            <div 
              key={category.id} 
              className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4"
              style={{ borderLeftColor: category.color || '#F59E0B' }}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-bold text-lg text-slate-900 dark:text-white">{category.name}</h4>
                  <p className="text-xs text-slate-400">Slug: {category.slug}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEditModal('category', category)}
                    className="p-1.5 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded"
                    title="Izmeni"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete('category', category.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Obriši"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Podkategorije:</span>
                  <button
                    onClick={() => openCreateModal('subcategory', category)}
                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    + Dodaj
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {category.subcategories?.map(sub => (
                    <span 
                      key={sub.id}
                      className="group inline-flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-sm"
                    >
                      {sub.name}
                      <button
                        onClick={() => openEditModal('subcategory', sub, category)}
                        className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete('subcategory', sub.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {(!category.subcategories || category.subcategories.length === 0) && (
                    <span className="text-sm text-slate-400 italic">Nema podkategorija</span>
                  )}
                </div>
              </div>
              
              {category.property_count !== undefined && (
                <div className="text-xs text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700">
                  {category.property_count} oglasa u ovoj kategoriji
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Fields Section
  const FieldsSection = () => {
    const [expandedField, setExpandedField] = useState<number | null>(null);
    
    const fieldTypeLabels: Record<string, string> = {
      text: 'Tekst',
      number: 'Broj',
      select: 'Izbor (jedan)',
      multiselect: 'Izbor (više)',
      boolean: 'Da/Ne',
      range: 'Opseg',
      textarea: 'Dugačak tekst',
      location: 'Lokacija'
    };

    const filterTypeLabels: Record<string, string> = {
      exact: 'Tačno',
      range: 'Od - Do',
      multiselect: 'Višestruki izbor',
      boolean: 'Da/Ne',
      text: 'Tekst'
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Polja / Filteri za oglase</h3>
          <button
            onClick={() => openCreateModal('field')}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo polje
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Naziv</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Slug</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Tip polja</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Tip filtera</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Obavezno</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {fields.map(field => (
                <React.Fragment key={field.id}>
                  <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 dark:text-white">{field.name}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{field.slug}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        {fieldTypeLabels[field.field_type] || field.field_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                        {filterTypeLabels[field.filter_type] || field.filter_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {field.is_required ? (
                        <span className="text-green-500">✓</span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {(field.field_type === 'select' || field.field_type === 'multiselect') && (
                          <button
                            onClick={() => setExpandedField(expandedField === field.id ? null : field.id)}
                            className="text-purple-500 hover:text-purple-600 text-sm font-medium"
                          >
                            {expandedField === field.id ? 'Sakrij opcije' : 'Opcije'}
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal('field', field)}
                          className="text-blue-500 hover:text-blue-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete('field', field.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedField === field.id && field.options && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3 bg-slate-50 dark:bg-slate-700/50">
                        <div className="pl-8">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Opcije za "{field.name}":</span>
                            <button
                              onClick={() => openCreateModal('fieldOption', field)}
                              className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                            >
                              + Dodaj opciju
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {field.options.map(opt => (
                              <span 
                                key={opt.id}
                                className="group inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded text-sm border border-slate-200 dark:border-slate-600"
                              >
                                {opt.label}
                                <button
                                  onClick={() => openEditModal('fieldOption', opt, field)}
                                  className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete('fieldOption', opt.id)}
                                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </span>
                            ))}
                            {field.options.length === 0 && (
                              <span className="text-sm text-slate-400 italic">Nema opcija</span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Field Mapping Section
  const FieldMappingSection = () => {
    const [selectedAdType, setSelectedAdType] = useState<number | 'all'>('all');
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [selectedFields, setSelectedFields] = useState<number[]>([]);
    const [requiredFields, setRequiredFields] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);

    // Get fields for the selected combination
    const currentMappings = categoryFields.filter(cf => {
      if (selectedCategory === null) return false;
      if (selectedAdType === 'all') {
        return cf.category_id === selectedCategory && cf.ad_type_id === null;
      }
      return cf.category_id === selectedCategory && cf.ad_type_id === selectedAdType;
    });

    // Load current mappings when selection changes
    useEffect(() => {
      if (selectedCategory !== null) {
        const mappedFieldIds = currentMappings.map(m => m.field_id);
        const requiredFieldIds = currentMappings.filter(m => m.is_required).map(m => m.field_id);
        setSelectedFields(mappedFieldIds);
        setRequiredFields(requiredFieldIds);
      }
    }, [selectedCategory, selectedAdType, categoryFields]);

    const handleToggleField = (fieldId: number) => {
      setSelectedFields(prev => 
        prev.includes(fieldId) 
          ? prev.filter(id => id !== fieldId)
          : [...prev, fieldId]
      );
      // Also remove from required if unchecking
      if (selectedFields.includes(fieldId)) {
        setRequiredFields(prev => prev.filter(id => id !== fieldId));
      }
    };

    const handleToggleRequired = (fieldId: number) => {
      if (!selectedFields.includes(fieldId)) return;
      setRequiredFields(prev => 
        prev.includes(fieldId) 
          ? prev.filter(id => id !== fieldId)
          : [...prev, fieldId]
      );
    };

    const handleSaveMappings = async () => {
      if (selectedCategory === null) {
        alert('Molimo izaberite kategoriju');
        return;
      }
      
      setSaving(true);
      try {
        const res = await client.api.fetch('/admin/category-fields', {
          method: 'POST',
          body: JSON.stringify({
            ad_type_id: selectedAdType === 'all' ? null : selectedAdType,
            category_id: selectedCategory,
            field_ids: selectedFields,
            required_field_ids: requiredFields
          })
        });
        const data = await res.json();
        if (data.success) {
          // Ažuriraj lokalni state umesto da ponovo fetchuješ
          // To sprečava resetovanje forme
          const newMappings = selectedFields.map(fieldId => ({
            category_id: selectedCategory,
            ad_type_id: selectedAdType === 'all' ? null : selectedAdType,
            field_id: fieldId,
            is_required: requiredFields.includes(fieldId)
          }));
          
          setCategoryFields(prev => {
            // Ukloni stara mapiranja za ovu kombinaciju
            const filtered = prev.filter(cf => 
              !(cf.category_id === selectedCategory && 
                (selectedAdType === 'all' ? cf.ad_type_id === null : cf.ad_type_id === selectedAdType))
            );
            // Dodaj nova mapiranja
            return [...filtered, ...newMappings];
          });
          
          alert('Mapiranje uspešno sačuvano!');
        } else {
          alert(data.error || 'Greška pri čuvanju');
        }
      } catch (err) {
        alert('Greška pri povezivanju sa serverom');
      } finally {
        setSaving(false);
      }
    };

    // Group fields by type for better organization
    const groupedFields = {
      basic: fields.filter(f => ['naslov', 'cena', 'lokacija', 'opis'].includes(f.slug)),
      numeric: fields.filter(f => f.field_type === 'number' && !['naslov', 'cena', 'lokacija', 'opis'].includes(f.slug)),
      select: fields.filter(f => ['select', 'multiselect'].includes(f.field_type)),
      boolean: fields.filter(f => f.field_type === 'boolean'),
      other: fields.filter(f => !['number', 'select', 'multiselect', 'boolean'].includes(f.field_type) && !['naslov', 'cena', 'lokacija', 'opis'].includes(f.slug))
    };

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Mapiranje polja po kategorijama i tipovima oglasa
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            Ovde možete konfigurisati koja polja su dostupna za svaku kombinaciju tipa oglasa i kategorije.
            Na primer, za Potražnju cena može biti opseg (od-do), dok je za Ponudu fiksna vrednost.
          </p>

          {/* Selection Row */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Tip oglasa
              </label>
              <select
                value={selectedAdType}
                onChange={(e) => setSelectedAdType(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="all">Svi tipovi (zajedničko)</option>
                <optgroup label="Za Ponudu / Aukciju">
                  {adTypes.filter(t => ['prodaja', 'izdavanje'].includes(t.slug)).map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </optgroup>
                <optgroup label="Za Potražnju">
                  {adTypes.filter(t => ['kupovina', 'rentiranje'].includes(t.slug)).map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </optgroup>
                {/* Ostali tipovi koji ne spadaju u grupe */}
                {adTypes.filter(t => !['prodaja', 'izdavanje', 'kupovina', 'rentiranje'].includes(t.slug)).map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                "Svi tipovi" definiše zajednička polja, a pojedinačni tipovi mogu imati dodatna/različita
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Kategorija
              </label>
              <select
                value={selectedCategory ?? ''}
                onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="">Izaberite kategoriju...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedCategory !== null ? (
            <>
              {/* Field Selection */}
              <div className="space-y-6">
                {/* Basic Fields */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Osnovna polja
                  </h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {groupedFields.basic.map(field => (
                      <div key={field.id} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field.id)}
                          onChange={() => handleToggleField(field.id)}
                          className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-900 dark:text-white text-sm">{field.name}</div>
                          <div className="text-xs text-slate-400">{field.filter_type === 'range' ? 'Opseg' : 'Tačno'}</div>
                        </div>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={requiredFields.includes(field.id)}
                            onChange={() => handleToggleRequired(field.id)}
                            disabled={!selectedFields.includes(field.id)}
                            className="w-3 h-3 text-red-500 border-slate-300 rounded focus:ring-red-500"
                          />
                          <span className="text-slate-500">Obavezno</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Numeric Fields */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Numerička polja
                  </h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {groupedFields.numeric.map(field => (
                      <div key={field.id} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field.id)}
                          onChange={() => handleToggleField(field.id)}
                          className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-900 dark:text-white text-sm">{field.name}</div>
                          <div className="text-xs text-slate-400">{field.filter_type === 'range' ? 'Opseg (od-do)' : 'Tačna vrednost'}</div>
                        </div>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={requiredFields.includes(field.id)}
                            onChange={() => handleToggleRequired(field.id)}
                            disabled={!selectedFields.includes(field.id)}
                            className="w-3 h-3 text-red-500 border-slate-300 rounded focus:ring-red-500"
                          />
                          <span className="text-slate-500">Obav.</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Select Fields */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    Polja sa opcijama
                  </h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {groupedFields.select.map(field => (
                      <div key={field.id} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field.id)}
                          onChange={() => handleToggleField(field.id)}
                          className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-slate-900 dark:text-white text-sm">{field.name}</div>
                          <div className="text-xs text-slate-400">{field.field_type === 'multiselect' ? 'Višestruki izbor' : 'Jedan izbor'}</div>
                        </div>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={requiredFields.includes(field.id)}
                            onChange={() => handleToggleRequired(field.id)}
                            disabled={!selectedFields.includes(field.id)}
                            className="w-3 h-3 text-red-500 border-slate-300 rounded focus:ring-red-500"
                          />
                          <span className="text-slate-500">Obav.</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Boolean Fields */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Da/Ne polja (karakteristike)
                  </h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {groupedFields.boolean.map(field => (
                      <div key={field.id} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field.id)}
                          onChange={() => handleToggleField(field.id)}
                          className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{field.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Other Fields */}
                {groupedFields.other.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-slate-500 rounded-full"></span>
                      Ostala polja
                    </h4>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {groupedFields.other.map(field => (
                        <div key={field.id} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(field.id)}
                            onChange={() => handleToggleField(field.id)}
                            className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900 dark:text-white text-sm">{field.name}</div>
                            <div className="text-xs text-slate-400">{field.field_type}</div>
                          </div>
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={requiredFields.includes(field.id)}
                              onChange={() => handleToggleRequired(field.id)}
                              disabled={!selectedFields.includes(field.id)}
                              className="w-3 h-3 text-red-500 border-slate-300 rounded focus:ring-red-500"
                            />
                            <span className="text-slate-500">Obav.</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Summary & Save */}
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Izabrano: <span className="font-semibold text-slate-700 dark:text-slate-300">{selectedFields.length}</span> polja
                  {requiredFields.length > 0 && (
                    <span className="ml-2">
                      (obaveznih: <span className="font-semibold text-red-500">{requiredFields.length}</span>)
                    </span>
                  )}
                </div>
                <button
                  onClick={handleSaveMappings}
                  disabled={saving}
                  className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Čuvanje...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Sačuvaj mapiranje
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p>Izaberite kategoriju da biste konfigurisali polja</p>
            </div>
          )}
        </div>

        {/* Quick Info Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
            <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">💡 Tip: Ponuda vs Potražnja</h4>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              Za Ponudu, cena je obično fiksna vrednost. Za Potražnju, korisnici navode raspon cena koji im odgovara.
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-100 dark:border-green-800">
            <h4 className="font-semibold text-green-700 dark:text-green-300 mb-2">📊 Različite kategorije</h4>
            <p className="text-sm text-green-600 dark:text-green-400">
              Za stanove su bitni sprat i lift, dok za placeve trebaju informacije o dozvoli za gradnju.
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
            <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-2">⚙️ Napredna podešavanja</h4>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              U sekciji "Polja/Filteri" možete promeniti tip filtera za svako polje (tačno/opseg).
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Modal Component
  const Modal = () => {
    if (!showModal) return null;

    const getTitle = () => {
      const action = editingItem ? 'Izmeni' : 'Kreiraj';
      switch (modalType) {
        case 'adType': return `${action} tip oglasa`;
        case 'category': return `${action} kategoriju`;
        case 'subcategory': return `${action} podkategoriju`;
        case 'field': return `${action} polje`;
        case 'fieldOption': return `${action} opciju`;
        default: return action;
      }
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">{getTitle()}</h3>
          
          <div className="space-y-4">
            {/* Common name field */}
            {modalType !== 'fieldOption' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Naziv *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  placeholder="Unesite naziv"
                />
              </div>
            )}

            {/* Slug field (not for fieldOption) */}
            {modalType !== 'fieldOption' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  value={formData.slug || ''}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  placeholder="Automatski iz naziva"
                />
              </div>
            )}

            {/* AdType specific fields */}
            {modalType === 'adType' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Opis
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  rows={3}
                />
              </div>
            )}

            {/* Category specific fields */}
            {modalType === 'category' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Ikona
                  </label>
                  <select
                    value={formData.icon || 'home'}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  >
                    <option value="home">🏠 Kuća</option>
                    <option value="building">🏢 Zgrada</option>
                    <option value="briefcase">💼 Poslovno</option>
                    <option value="map">🗺️ Zemljište</option>
                    <option value="car">🚗 Garaža</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Boja
                  </label>
                  <input
                    type="color"
                    value={formData.color || '#F59E0B'}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 cursor-pointer"
                  />
                </div>
              </>
            )}

            {/* Field specific fields */}
            {modalType === 'field' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Tip polja
                  </label>
                  <select
                    value={formData.field_type || 'text'}
                    onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  >
                    <option value="text">Tekst</option>
                    <option value="number">Broj</option>
                    <option value="select">Izbor (jedan)</option>
                    <option value="multiselect">Izbor (više)</option>
                    <option value="boolean">Da/Ne</option>
                    <option value="textarea">Dugačak tekst</option>
                    <option value="location">Lokacija</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Tip filtera
                  </label>
                  <select
                    value={formData.filter_type || 'exact'}
                    onChange={(e) => setFormData({ ...formData, filter_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  >
                    <option value="exact">Tačno podudaranje</option>
                    <option value="range">Od - Do (opseg)</option>
                    <option value="multiselect">Višestruki izbor</option>
                    <option value="boolean">Da/Ne</option>
                    <option value="text">Tekstualna pretraga</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isRequired"
                    checked={formData.is_required || false}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                    className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="isRequired" className="text-sm text-slate-700 dark:text-slate-300">
                    Obavezno polje
                  </label>
                </div>
              </>
            )}

            {/* Field Option specific fields */}
            {modalType === 'fieldOption' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Vrednost
                  </label>
                  <input
                    type="text"
                    value={formData.value || ''}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    placeholder="npr. centralno"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Oznaka (label)
                  </label>
                  <input
                    type="text"
                    value={formData.label || ''}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    placeholder="npr. Centralno grejanje"
                  />
                </div>
              </>
            )}

            {/* Sort order */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Redosled
              </label>
              <input
                type="number"
                value={formData.sort_order || 0}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                min="0"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            >
              Otkaži
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
            >
              {loading ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !adTypes.length && !categories.length && !fields.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <div className="text-red-600 dark:text-red-400 font-medium mb-2">Greška</div>
        <div className="text-red-500 dark:text-red-300 mb-4">{error}</div>
        <button 
          onClick={() => {
            setError(null);
            setLoading(true);
            Promise.all([fetchAdTypes(), fetchCategories(), fetchFields(), fetchCategoryFields()])
              .finally(() => setLoading(false));
          }}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Pokušaj ponovo
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionTabs />
      
      {activeSection === 'ad-types' && <AdTypesSection />}
      {activeSection === 'categories' && <CategoriesSection />}
      {activeSection === 'fields' && <FieldsSection />}
      {activeSection === 'field-mapping' && <FieldMappingSection />}
      
      <Modal />
    </div>
  );
}